import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { AlertCircle, CheckCircle, Clock, CalendarX } from 'lucide-react';
import { 
  isEmployeeFullyApproved, 
  getDocumentValidityInfo, 
  formatDate 
} from './constants';

const EmployeeSelector = ({ 
  employees, 
  selectedIds, 
  onToggle, 
  permitEndDate 
}) => {
  // Personeli tarih aralığına göre kategorize et
  const categorizeEmployees = () => {
    // Aktif ve onaylı personeller
    const approvedEmps = employees.filter(e => e.is_active !== false && isEmployeeFullyApproved(e));
    
    // Tarih aralığında belgesi geçerli olanlar - seçilebilir
    const validForDateRange = [];
    // Tarih aralığında belgesi dolacak olanlar - seçilemez, uyarı göster
    const expiringInDateRange = [];
    
    approvedEmps.forEach(emp => {
      const validityInfo = getDocumentValidityInfo(emp, permitEndDate);
      if (validityInfo.isValid) {
        validForDateRange.push(emp);
      } else {
        expiringInDateRange.push({
          employee: emp,
          expiringDocs: validityInfo.expiringDocs
        });
      }
    });
    
    // Aktif ama onaysız personeller - seçilemez
    const unapproved = employees.filter(e => e.is_active !== false && !isEmployeeFullyApproved(e));
    // Pasif personeller - seçilemez
    const passive = employees.filter(e => e.is_active === false);
    
    return { validForDateRange, expiringInDateRange, unapproved, passive };
  };

  if (!employees || employees.length === 0) {
    return (
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <p className="text-sm text-slate-500">
          Bu firmaya kayıtlı personel bulunmamaktadır.
        </p>
      </div>
    );
  }

  if (!permitEndDate) {
    return (
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          Personel listesini görmek için önce bitiş tarihini seçin.
        </p>
      </div>
    );
  }

  const { validForDateRange, expiringInDateRange, unapproved, passive } = categorizeEmployees();

  return (
    <div className="space-y-2">
      <Label>Çalışacak Personel</Label>
      <div className="border rounded-lg p-4 max-h-80 overflow-y-auto space-y-3">
        {/* Belge süresi dolacak personeller - UYARI */}
        {expiringInDateRange.length > 0 && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
            <p className="text-sm font-medium text-orange-700 flex items-center gap-2 mb-2">
              <CalendarX className="h-4 w-4" />
              Belge Süresi Dolacak Personel ({expiringInDateRange.length})
            </p>
            <p className="text-xs text-orange-600 mb-3">
              Aşağıdaki personelin belgeleri seçilen tarih aralığında dolacağından çalışma iznine eklenemezler:
            </p>
            <div className="space-y-2">
              {expiringInDateRange.map(({ employee, expiringDocs }) => (
                <div key={employee.id} className="flex items-start gap-2 text-orange-800 bg-orange-100/50 p-2 rounded">
                  <span className="font-medium text-sm">
                    {employee.name} {employee.surname}
                    {employee.tc_kimlik_no && (
                      <span className="font-mono text-xs ml-1">({employee.tc_kimlik_no})</span>
                    )}
                  </span>
                  <span className="text-xs text-orange-600">
                    - {expiringDocs.map(d => `${d.docName} (${formatDate(d.expiryDate)})`).join(', ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Geçerli Personel - Checkbox ile */}
        {validForDateRange.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-emerald-600 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Seçilebilir Personel ({validForDateRange.length})
            </p>
            {validForDateRange.map((employee) => (
              <div key={employee.id} className="flex items-center gap-2 pl-2">
                <Checkbox
                  id={employee.id}
                  checked={selectedIds?.includes(employee.id)}
                  onCheckedChange={() => onToggle(employee.id)}
                />
                <Label htmlFor={employee.id} className="font-normal cursor-pointer text-slate-900">
                  {employee.name} {employee.surname}
                  {employee.tc_kimlik_no && (
                    <span className="text-slate-500 ml-1 font-mono text-xs">({employee.tc_kimlik_no})</span>
                  )}
                  {employee.position && (
                    <span className="text-slate-400 ml-1 text-xs">- {employee.position}</span>
                  )}
                </Label>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Seçilen tarih aralığında belgeleri geçerli personel bulunmamaktadır. 
              {expiringInDateRange.length > 0 && ' Yukarıdaki personellerin belgelerini yenileyebilirsiniz.'}
            </p>
          </div>
        )}
        
        {/* Onaysız Personel - Checkbox olmadan */}
        {unapproved.length > 0 && (
          <div className="space-y-2 mt-4 pt-4 border-t">
            <p className="text-xs font-medium text-amber-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Belgeleri Eksik Personel ({unapproved.length})
            </p>
            {unapproved.map((employee) => (
              <div key={employee.id} className="flex items-center gap-2 pl-2 text-slate-400">
                <span className="w-4 h-4 flex items-center justify-center text-xs">•</span>
                <span>
                  {employee.name} {employee.surname}
                  {employee.tc_kimlik_no && (
                    <span className="font-mono text-xs ml-1">({employee.tc_kimlik_no})</span>
                  )}
                  {employee.position && (
                    <span className="ml-1 text-xs">- {employee.position}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {/* Pasif Personel - Checkbox olmadan */}
        {passive.length > 0 && (
          <div className="space-y-2 mt-4 pt-4 border-t">
            <p className="text-xs font-medium text-slate-400 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Pasif Personel ({passive.length})
            </p>
            {passive.map((employee) => (
              <div key={employee.id} className="flex items-center gap-2 pl-2 text-slate-300">
                <span className="w-4 h-4 flex items-center justify-center text-xs">•</span>
                <span>
                  {employee.name} {employee.surname}
                  {employee.tc_kimlik_no && (
                    <span className="font-mono text-xs ml-1">({employee.tc_kimlik_no})</span>
                  )}
                  {employee.position && (
                    <span className="ml-1 text-xs">- {employee.position}</span>
                  )}
                  <Badge className="ml-2 bg-slate-100 text-slate-400 text-xs">Pasif</Badge>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeSelector;
